import EntityMember from "_shared/entity-members/entity-member";
import EntityMemberSchema from "_shared/entity-members/entity-member-schema";
import { z } from "zod";

export const ProposalTeamReuseJSONZ = z.object({
  leftTheirOrganization: z.array(EntityMemberSchema.EntityMemberJSONZ),
  leadersWhoCannotSign: z.array(EntityMemberSchema.EntityMemberJSONZ),
});

export type ProposalTeamReuseJSON = z.infer<typeof ProposalTeamReuseJSONZ>;

/**
 * Who on a proposal's teams a new draft started from it must not carry over (#1569).
 *
 * The people on a proposal were right when it was written. Since then some may have left the
 * organization they were on the team for, and a leader may have lost the authority to sign for it.
 * Only the server can tell, and it answers only for the teams the reader can see on that proposal.
 */
export default class ProposalTeamReuse {
  /** Nobody to leave out: what a proposal reads as when every person on it can still be carried over. */
  public static readonly NOBODY_TO_LEAVE_OUT = new ProposalTeamReuse({
    leftTheirOrganization: [],
    leadersWhoCannotSign: [],
  });

  public static fromJSON(json: ProposalTeamReuseJSON): ProposalTeamReuse {
    const parsed = ProposalTeamReuseJSONZ.parse(json);
    return new ProposalTeamReuse({
      leftTheirOrganization: parsed.leftTheirOrganization.map(toEntityMember),
      leadersWhoCannotSign: parsed.leadersWhoCannotSign.map(toEntityMember),
    });
  }

  public readonly leftTheirOrganization: readonly EntityMember[];
  public readonly leadersWhoCannotSign: readonly EntityMember[];

  public constructor(params: { leftTheirOrganization: EntityMember[]; leadersWhoCannotSign: EntityMember[] }) {
    this.leftTheirOrganization = [...params.leftTheirOrganization];
    this.leadersWhoCannotSign = [...params.leadersWhoCannotSign];
  }

  /** Whether `member` has left the organization they were on the team for. */
  public hasLeft(member: EntityMember): boolean {
    return this.leftTheirOrganization.some((departed) => departed.isEqualTo(member));
  }

  /** Whether `leader` can lead a team in the new draft: still at their organization and able to sign for it. */
  public canLead(leader: EntityMember): boolean {
    return !this.hasLeft(leader) && !this.leadersWhoCannotSign.some((other) => other.isEqualTo(leader));
  }

  public toJSON(): ProposalTeamReuseJSON {
    return {
      leftTheirOrganization: this.leftTheirOrganization.map((member) => member.toJSON()),
      leadersWhoCannotSign: this.leadersWhoCannotSign.map((member) => member.toJSON()),
    };
  }
}

function toEntityMember(json: z.infer<typeof EntityMemberSchema.EntityMemberJSONZ>): EntityMember {
  const member = EntityMember.fromJSON(json);
  if (!member) throw new Error("A team member is required.");
  return member;
}
